/**
 * BalanceProvenanceCard — Phase 85.1
 *
 * Shows WHERE a liability's balance came from, and keeps the reported balance
 * visually separate from any estimate. In Rollout 1 there is no estimate yet,
 * so this card explains what 2120 still needs before it can produce one.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { formatMoney } from '@/lib/format';
import { reportLiabilityBalance } from '@/lib/liabilityReconcileApi';

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

const BLOCKED_COPY = {
  missing_interest_rate:
    "Add this loan's interest rate so 2120 can work out how much of each payment reduces the balance.",
  missing_reported_balance:
    'Enter the outstanding balance your lender last reported.',
  missing_reported_balance_date:
    'Add the date of the balance your lender reported.',
  emi_includes_insurance:
    'This payment includes insurance, so the principal portion cannot be worked out reliably.',
};

function blockedCopy(reason) {
  if (!reason) return null;
  if (BLOCKED_COPY[reason]) return BLOCKED_COPY[reason];
  if (reason.startsWith('unsupported_interest_method')) {
    return "This loan's interest method isn't supported for estimates yet.";
  }
  return null;
}

export function BalanceProvenanceCard({
  liabilityId, balance, linkedPaymentCount = 0, onReviewAssumptions, onUpdated,
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  if (!balance) return null;
  const ccy = balance.currency || 'INR';
  const hasEstimate = balance.estimatedOutstanding != null;

  const save = async () => {
    const n = Number(value);
    if (!Number.isFinite(n) || n < 0) {
      toast.error('Enter the balance as a number.');
      return;
    }
    setSaving(true);
    try {
      const res = await reportLiabilityBalance(liabilityId, n);
      toast.success('Balance updated from your lender.');
      setEditing(false);
      setValue('');
      onUpdated?.(res?.balance);
    } catch {
      toast.error('Could not save that balance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid="liab-balance-provenance">
      <p className="text-xs font-semibold text-charcoal mb-3">Balance provenance</p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div data-testid="liab-balance-reported">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
            Reported outstanding
          </p>
          <p className="num font-display text-xl font-medium text-charcoal">
            {formatMoney(balance.reportedOutstanding || 0, ccy)}
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            as of {fmtDate(balance.reportedAsOf)}
          </p>
        </div>

        <div data-testid="liab-balance-estimated">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-medium mb-1">
            Estimated outstanding
            {hasEstimate && (
              <span
                className="ml-1.5 rounded-full bg-evergreen/10 px-1.5 py-0.5 text-[9px] tracking-normal text-evergreen font-semibold"
                data-testid="liab-balance-estimated-chip"
              >
                2120 estimate
              </span>
            )}
          </p>
          {hasEstimate ? (
            <>
              <p className="num font-display text-xl font-medium text-charcoal">
                {formatMoney(balance.estimatedOutstanding, ccy)}
              </p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                as of {fmtDate(balance.estimatedAsOf)} · confidence {balance.confidence}
              </p>
            </>
          ) : (
            <>
              <p className="num font-display text-xl font-medium text-muted-foreground">—</p>
              <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="liab-balance-estimate-note">
                {linkedPaymentCount > 0
                  ? 'Payments detected, but 2120 needs the loan’s interest details to estimate the updated balance.'
                  : 'No estimate yet — 2120 only shows balances you or your lender reported.'}
              </p>
            </>
          )}
        </div>
      </div>

      {blockedCopy(balance.estimateBlockedReason) && (
        <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed" data-testid="liab-balance-blocked-reason">
          {blockedCopy(balance.estimateBlockedReason)}
        </p>
      )}

      {editing ? (
        <div className="flex items-center gap-2 mt-4">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Current balance from your lender"
            className="h-9 max-w-xs"
            data-testid="liab-balance-input"
          />
          <Button size="sm" onClick={save} disabled={saving} data-testid="liab-balance-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)} data-testid="liab-balance-cancel">
            Cancel
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2 mt-4">
          <Button size="sm" variant="secondary" onClick={() => setEditing(true)} data-testid="liab-balance-enter-lender">
            Enter current lender balance
          </Button>
          {hasEstimate && (
            <Button
              size="sm" variant="secondary"
              onClick={async () => {
                setSaving(true);
                try {
                  const res = await reportLiabilityBalance(liabilityId, balance.estimatedOutstanding);
                  toast.success('Estimated balance confirmed as reported.');
                  onUpdated?.(res?.balance);
                } catch {
                  toast.error('Could not confirm that balance.');
                } finally {
                  setSaving(false);
                }
              }}
              data-testid="liab-balance-confirm-estimate"
            >
              Confirm estimated balance
            </Button>
          )}
          {onReviewAssumptions && (
            <Button size="sm" variant="ghost" onClick={onReviewAssumptions} data-testid="liab-balance-review-assumptions">
              Review loan assumptions
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default BalanceProvenanceCard;
